/**
 * The fluorescent hum. Level 0's defining sound and the one thing that plays
 * for the entire run.
 *
 * A failing magnetic ballast does not produce a tone, it produces the mains
 * harmonic series buzzing through a laminated core: a fundamental at twice
 * mains frequency, a long tail of harmonics, and a fine hiss from the tube
 * itself. Three copies a few cents apart beat against each other, which is what
 * makes a real fixture *irritating* rather than musical, and two very slow LFOs
 * keep the level wandering so it never settles into a loop the ear can dismiss.
 * Every so often the arc stutters.
 *
 * It has to be quiet enough to live under everything else and still be the
 * thing the player would notice if it stopped.
 */

import { TUNING } from '../core/config';
import { clamp01 } from '../core/math';
import type { RNG } from '../core/types';
import type { AudioGraph } from './graph';
import { makeHarmonicWave } from './noise';

/** Relative amplitudes of the ballast's harmonic series. */
const PARTIALS = [1, 0.62, 0.34, 0.24, 0.16, 0.12, 0.085, 0.062, 0.045, 0.033, 0.025, 0.019, 0.014, 0.011, 0.009];

const DETUNES = [-7, 3.5, 11.5];
const LEVELS = [0.03, 0.02, 0.013];

export class Hum {
  /** Layer level, 0..1. Owned by whoever built the hum. */
  readonly out: GainNode;

  private readonly ctx: AudioContext;
  private readonly rng: RNG;
  private readonly oscs: OscillatorNode[] = [];
  private readonly started: AudioScheduledSourceNode[] = [];
  private readonly toneFilter: BiquadFilterNode;
  private readonly stutter: GainNode;
  private readonly sizzle: GainNode;
  private readonly ring: GainNode;

  private stutterIn: number;
  private unease = 0;
  private appliedUnease = -1;

  /**
   * `variant` shifts the fixture a few hertz off nominal so different levels do
   * not hum in unison; `flicker` scales how often the arc stutters.
   */
  constructor(
    private readonly g: AudioGraph,
    dest: AudioNode,
    variant = 0,
    private readonly flicker = 1,
  ) {
    const ctx = g.ctx;
    this.ctx = ctx;
    this.rng = g.rng;

    this.out = ctx.createGain();
    this.out.gain.value = 0;

    this.stutter = ctx.createGain();
    this.stutter.gain.value = 1;
    this.stutter.connect(this.out);
    this.out.connect(dest);

    // Slow amplitude wander. Two incommensurate rates never repeat audibly.
    const drift = ctx.createGain();
    drift.gain.value = 1;
    drift.connect(this.stutter);
    this.lfo(0.043 + variant * 0.004, 0.17, drift.gain);
    this.lfo(0.117, 0.09, drift.gain);

    // A peak around 700Hz is where the buzz actually cuts through.
    const peak = ctx.createBiquadFilter();
    peak.type = 'peaking';
    peak.frequency.value = 700;
    peak.Q.value = 1.4;
    peak.gain.value = 5;
    peak.connect(drift);

    this.toneFilter = ctx.createBiquadFilter();
    this.toneFilter.type = 'lowpass';
    this.toneFilter.frequency.value = 2600;
    this.toneFilter.Q.value = 0.6;
    this.toneFilter.connect(peak);
    this.lfo(0.031, 600, this.toneFilter.frequency);

    const f = TUNING.audio.humFrequency + variant * 1.7;
    const wave = makeHarmonicWave(ctx, PARTIALS);
    for (let i = 0; i < DETUNES.length; i++) {
      const osc = ctx.createOscillator();
      osc.setPeriodicWave(wave);
      osc.frequency.value = f;
      osc.detune.value = DETUNES[i]!;
      const gain = ctx.createGain();
      gain.gain.value = LEVELS[i]!;
      osc.connect(gain);
      gain.connect(this.toneFilter);
      this.oscs.push(osc);
      this.start(osc);
    }

    // Mains fundamental under the ballast. Felt more than heard.
    const sub = ctx.createOscillator();
    sub.type = 'sine';
    sub.frequency.value = f * 0.5;
    const subGain = ctx.createGain();
    subGain.gain.value = 0.012;
    sub.connect(subGain);
    subGain.connect(drift);
    this.start(sub);

    // Tube hiss.
    const noise = ctx.createBufferSource();
    noise.buffer = g.pink;
    noise.loop = true;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 5200;
    bp.Q.value = 1.8;
    this.sizzle = ctx.createGain();
    this.sizzle.gain.value = 0.02;
    noise.connect(bp);
    bp.connect(this.sizzle);
    this.sizzle.connect(drift);
    this.lfo(0.071, 0.008, this.sizzle.gain);
    this.start(noise, this.rng.range(0, 2));

    // Tinnitus. Silent until the player's sanity starts to go.
    const ringOsc = ctx.createOscillator();
    ringOsc.type = 'sine';
    ringOsc.frequency.value = 4180;
    this.ring = ctx.createGain();
    this.ring.gain.value = 0;
    ringOsc.connect(this.ring);
    this.ring.connect(this.out);
    this.start(ringOsc);

    this.stutterIn = this.rng.range(6, 20);
  }

  /** 0..1. Cross-faded by the ambience bed that owns this hum. */
  setLevel(level: number, seconds = 0): void {
    const t = this.g.now;
    const p = this.out.gain;
    p.cancelScheduledValues(t);
    p.setValueAtTime(p.value, t);
    if (seconds > 0) p.linearRampToValueAtTime(clamp01(level), t + seconds);
    else p.setValueAtTime(clamp01(level), t);
  }

  /**
   * 0..1, rising as the player's sanity falls. Widens the beating and brings in
   * a high ring, so the room the player has been listening to for ten minutes
   * quietly stops being the same room.
   */
  setUnease(v: number): void {
    this.unease = clamp01(v);
  }

  update(dt: number): void {
    if (Math.abs(this.unease - this.appliedUnease) > 0.02) {
      this.appliedUnease = this.unease;
      const t = this.g.now;
      for (let i = 0; i < this.oscs.length; i++) {
        const base = DETUNES[i]!;
        this.oscs[i]!.detune.setTargetAtTime(base * (1 + this.unease * 2.6), t, 0.8);
      }
      this.ring.gain.setTargetAtTime(this.unease * this.unease * 0.006, t, 1.5);
      this.sizzle.gain.setTargetAtTime(0.02 + this.unease * 0.014, t, 1.2);
    }

    this.stutterIn -= dt * this.flicker;
    if (this.stutterIn <= 0) {
      this.scheduleStutter();
      this.stutterIn = this.rng.range(11, 42) / Math.max(0.1, this.flicker);
    }
  }

  dispose(): void {
    const t = this.g.now;
    for (const s of this.started) {
      try {
        s.stop(t);
      } catch {
        /* already stopped */
      }
    }
    this.started.length = 0;
    this.out.disconnect();
  }

  /** The arc losing its footing for a fraction of a second. */
  private scheduleStutter(): void {
    const p = this.stutter.gain;
    let t = this.g.now + this.rng.range(0, 0.4);
    p.cancelScheduledValues(t);
    const n = this.rng.int(2, 5);
    for (let i = 0; i < n; i++) {
      p.setValueAtTime(1, t);
      p.linearRampToValueAtTime(this.rng.range(0.12, 0.5), t + 0.012);
      t += 0.012 + this.rng.range(0.02, 0.1);
      p.linearRampToValueAtTime(1, t);
      t += this.rng.range(0.03, 0.16);
    }
    p.setValueAtTime(1, t);
  }

  private lfo(rate: number, depth: number, target: AudioParam): void {
    const osc = this.ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = rate;
    const amp = this.ctx.createGain();
    amp.gain.value = depth;
    osc.connect(amp);
    amp.connect(target);
    this.start(osc);
  }

  private start(node: AudioScheduledSourceNode, offset = 0): void {
    const t = this.g.now;
    if (node instanceof AudioBufferSourceNode) node.start(t, offset);
    else node.start(t);
    this.started.push(node);
  }
}
